import React from "react";
import { useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MultiSelect from "react-multi-select-component";
import { getTransactionReportSearchData } from "./../../stores/services/report.service";
import moment from "moment";
import { PDFExport } from "@progress/kendo-react-pdf";
import { BsTable } from "react-icons/bs";
import { getAmc } from "../../stores/services/amc.service";
import { getFundByAmc } from "../../stores/services/funds.service";
import ReportHeader from "./report-header";
import {
  sortAMCS,
  isDateValid,
  numberWithCommas,
} from "../../utils/customFunction";
import Select from "react-select";
import useCustomStyles from "../../hooks/useCustomStyles";

/**
 * VPS Transaction Report
 * -----------------------
 * This screen follows the exact same fetch/search pattern as the generic
 * TransactionReportScreen (getTransactionReportSearchData with `fields`),
 * but is scoped to only the three VPS transaction types requested:
 *   - investment          -> "Investment of Amount"
 *   - withdrawal          -> "Withdrawal"
 *   - changeofallocation  -> "Change of Allocation"
 *
 * The key difference vs. the generic report is that every VPS transaction
 * carries a `systematic_investment` object describing how the amount was
 * split across sub-funds (MM / DEBT / SF / etc). Rather than expanding that
 * into extra rows (like the debtmarketinvestment/transferofunits special
 * cases in the generic screen), we render the whole breakdown as a compact
 * nested table INSIDE a single "VPS Fund Allocation" cell so one transaction
 * = one row, matching the existing report's row-per-transaction convention.
 */

// ---------------------------------------------------------------------------
// Restricted txn type list (dropdown options) — only these 3 are selectable.
// ---------------------------------------------------------------------------
const VPS_TXN_TYPES: { api_name: string; dis_name: string }[] = [
  { api_name: "investment", dis_name: "Investment of Amount" },
  { api_name: "withdrawal", dis_name: "Withdrawal" },
  { api_name: "changeofallocation", dis_name: "Change of Allocation" },
];

// ---------------------------------------------------------------------------
// Column sets shown in the MultiSelect + table header, per txn type.
// (These are the "flat" transaction fields — NOT the nested allocation data,
// which always renders in its own dedicated column regardless of selection.)
// ---------------------------------------------------------------------------
const VPS_COLUMNS: Record<string, { label: string; value: string }[]> = {
  investment: [
    { label: "Txn ID", value: "txn_id" },
    { label: "Folio No", value: "folio_no" },
    { label: "Unitholder Name", value: "unitholder_name" },
    { label: "Made By", value: "made_by" },
    { label: "Status", value: "txn_status" },
    { label: "Source Portal", value: "source_portal" },
    { label: "Settlement Date", value: "settlement_date" },
    { label: "Gross Amount", value: "gross_amount" },
    { label: "Net Amount", value: "net_amount" },
    { label: "Units", value: "units" },
    { label: "Sale Load", value: "sale_load" },
    { label: "Allocation Scheme", value: "allocation_scheme" },
    { label: "Investment Amount", value: "investment_amount" },
  ],
  withdrawal: [
    { label: "Txn ID", value: "txn_id" },
    { label: "Folio No", value: "folio_no" },
    { label: "Unitholder Name", value: "unitholder_name" },
    { label: "Made By", value: "made_by" },
    { label: "Status", value: "txn_status" },
    { label: "Source Portal", value: "source_portal" },
    { label: "Settlement Date", value: "settlement_date" },
    { label: "Gross Amount", value: "gross_amount" },
    { label: "Net Amount", value: "net_amount" },
    { label: "Units", value: "units" },
    { label: "Tax CGT", value: "tax_cgt" },
    { label: "Tax WHT (IT)", value: "tax_wht_it" },
    { label: "Allocation Scheme", value: "allocation_scheme" },
    { label: "Investment Amount", value: "investment_amount" },
  ],
  changeofallocation: [
    { label: "Txn ID", value: "txn_id" },
    { label: "Folio No", value: "folio_no" },
    { label: "Unitholder Name", value: "unitholder_name" },
    { label: "Made By", value: "made_by" },
    { label: "Status", value: "txn_status" },
    { label: "Source Portal", value: "source_portal" },
    { label: "Settlement Date", value: "settlement_date" },
    { label: "From Scheme", value: "from_scheme" },
    { label: "To Scheme", value: "to_scheme" },
    { label: "Units", value: "units" },
    { label: "Gross Amount", value: "gross_amount" },
    { label: "Net Amount", value: "net_amount" },
    { label: "Allocation Scheme", value: "allocation_scheme" },
    { label: "Investment Amount", value: "investment_amount" },
  ],
};

// Fields that need number formatting / are summed in the footer totals row.
const NUMERIC_FIELDS = new Set([
  "gross_amount",
  "net_amount",
  "units",
  "sale_load",
  "tax_cgt",
  "tax_wht_it",
]);
const TOTAL_ROW_FIELDS = [
  "gross_amount",
  "net_amount",
  "units",
  "sale_load",
  "tax_cgt",
  "tax_wht_it",
];

// Fields that need date formatting.
const DATE_FIELDS = new Set([
  "instruction_date",
  "execution_date",
  "settlement_date",
  "executed_date",
  "trade_date",
]);

const VpsTransactionReportScreen = (props: any) => {
  const pdfExportComponent = React.useRef<PDFExport>(null);

  const [loading, setLoading] = useState(false);
  const email = sessionStorage.getItem("email") || "";
  const date = new Date();

  // ---- search results / lookups ----
  const [reportTxn, setReportTxn] = useState<any>([]);
  const [allAmc, setAllAmc] = useState<any>([]);
  const [allFunds, setAllFunds] = useState<any>([]);
  const [amcLabel, setAmcLabel] = useState<any>("");
  const { customStyles } = useCustomStyles();

  // ---- filter fields (same shape/pattern as the generic report) ----
  const [fields, setFields] = useState({
    amc_code: "",
    fund_code: "",
    from_date: moment(date).format("YYYY-MM-DD"),
    to_date: moment(date).format("YYYY-MM-DD"),
    txn_type: "",
    status: "",
  });

  // ---- resolved values used only in the printed report header block ----
  const [resAmc, setResAmc] = useState("");
  const [resFund, setResFund] = useState("");
  const [fromDateRes, setFromDateRes] = useState("");
  const [toDateRes, setToDateRes] = useState("");
  const [txnTypeRes, setTxnTypeRes] = useState("");
  const [statusRes, setStatusRes] = useState("");

  // ---- column selection (MultiSelect), same pattern as generic report ----
  const [dropdownList, setDropdownList] = useState<any>([]);
  const [allDropdownColumns, setAllDropdownColumns] = useState<any>([]);

  // ---- footer totals ----
  const [totals, setTotals] = useState<Record<string, number>>({});

  React.useEffect(() => {
    const getAllAMC = async () => {
      try {
        const amcResponse = await getAmc(email, "", "", "");
        const sortedAMCs = await sortAMCS(amcResponse.data.data);
        const options = sortedAMCs.map((amc: any) => ({
          ...amc,
          value: amc.amc_code,
          label: `${amc.name} (${amc.amc_code})`,
          name: amc.name,
        }));
        setAllAmc(options);
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message);
      }
    };
    getAllAMC();
  }, []);

  const getFunds = async (code: any) => {
    try {
      const response = await getFundByAmc(email, code);
      setAllFunds(response.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const renderFundsDropDown = () =>
    allFunds.map((item: any, index: number) => (
      <option key={index} value={item.symbol_code.replace("FUND_", "")}>
        {item.fund_name} - {item.symbol_code.replace("FUND_", "")}
      </option>
    ));

  const CapTxnType = (type: string) => {
    const found = VPS_TXN_TYPES.find((t) => t.api_name === type);
    return found ? found.dis_name : type;
  };

  // -------------------------------------------------------------------------
  // Flat-column cell renderer (mirrors renderDropdownValues in the generic
  // report but restricted to the fields actually relevant to VPS txns).
  // -------------------------------------------------------------------------
  const formatCellValue = (colValue: string, transaction: any) => {
    const val = transaction[colValue];

    if (DATE_FIELDS.has(colValue)) {
      return val ? isDateValid(moment(val).format("YYYY-MM-DD")) : "-";
    }

    if (colValue === "allocation_scheme") {
      const scheme = transaction.systematic_investment?.allocation_scheme;
      return scheme || val || "-";
    }

    if (colValue === "investment_amount") {
      const amt =
        transaction.systematic_investment?.investment_amount ??
        transaction.systematic_investment?.current_investment;
      return amt !== undefined && amt !== null
        ? numberWithCommas(parseFloat(amt || '0').toFixed(2))
        : "-";
    }

    if (NUMERIC_FIELDS.has(colValue)) {
      return val !== undefined && val !== "" && val !== null
        ? numberWithCommas(parseFloat(val).toFixed(2))
        : "-";
    }

    return val !== undefined && val !== "" && val !== null ? String(val) : "-";
  };

  // -------------------------------------------------------------------------
  // Renders the systematic_investment breakdown for ONE transaction as a
  // compact nested table so it stays inside a single row/cell.
  //
  // Handles both shapes seen in the data:
  //   A) "standard" subFunds: { name/fund_code, proportion, amount, units,
  //      nav, holding, allocation_type (conversionin/out), amount_in/out }
  //   B) "conversion mapping" subFunds used by some changeofallocation
  //      records: { from_fund, to_fund, pct, amount, from_fund_unit,
  //      to_fund_unit, from_fund_nav, to_fund_nav }
  // -------------------------------------------------------------------------
  const renderSubFundTable = (block: any, keyPrefix: string) => {
    const subFunds = Array.isArray(block?.subFunds) ? block.subFunds : [];
    if (subFunds.length === 0) return null;

    return (
      <table
        key={keyPrefix}
        style={{
          width: "100%",
          fontSize: "10px",
          borderCollapse: "collapse",
          marginBottom: "4px",
        }}
      >
        <thead>
          <tr style={{ background: "#f2f2f2" }}>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
              Fund
            </th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px" }}>%</th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
              Amount
            </th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
              Units
            </th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
              NAV
            </th>
          </tr>
        </thead>
        <tbody>
          {subFunds.map((sf: any, i: number) => {
            const isConversionPair = sf.from_fund && sf.to_fund;

            if (isConversionPair) {
              return (
                <tr key={i}>
                  <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                    {sf.from_fund} → {sf.to_fund}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                    {sf.pct !== undefined ? `${sf.pct}%` : "-"}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                    {numberWithCommas(parseFloat(sf.amount ?? "0").toFixed(2))}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                    {numberWithCommas(
                      parseFloat(sf.to_fund_unit ?? sf.from_fund_unit ?? "0").toFixed(2),
                    )}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                    {sf.to_fund_nav ?? sf.from_fund_nav ?? "-"}
                  </td>
                </tr>
              );
            }

            const label = `${sf.name || sf.fund_code || "-"}${
              sf.allocation_type
                ? ` (${sf.allocation_type === "conversionin" ? "In" : "Out"})`
                : ""
            }`;
            const amount = sf.amount ?? sf.amount_in ?? sf.amount_out ?? "0";

            return (
              <tr key={i}>
                <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                  {label}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                  {sf.proportion !== undefined ? `${sf.proportion}%` : "-"}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                  {numberWithCommas(parseFloat(amount).toFixed(2))}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                  {numberWithCommas(parseFloat(sf.units ?? "0").toFixed(2))}
                </td>
                <td style={{ border: "1px solid #ddd", padding: "2px 4px" }}>
                  {numberWithCommas(parseFloat(sf.nav ?? "-").toFixed(2))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderVpsAllocation = (transaction: any) => {
    const si = transaction.systematic_investment;
    if (
      !si ||
      !Array.isArray(si.investment_blocks) ||
      si.investment_blocks.length === 0
    ) {
      return <span className="text-muted">N/A</span>;
    }

    return (
      <div style={{ minWidth: "320px" }}>
        <div
          style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "2px" }}
        >
          Scheme: {si.allocation_scheme || "-"} | Total:{" "}
          {numberWithCommas(
            parseFloat(si.investment_amount ?? si.current_investment ?? "0").toFixed(2),
          )}
        </div>

        {si.investment_blocks.map((block: any, idx: number) =>
          renderSubFundTable(block, `cur-${idx}`),
        )}

        {transaction.txnr_type === "changeofallocation" &&
          Array.isArray(si.previous_allocation) &&
          si.previous_allocation.length > 0 && (
            <>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  margin: "4px 0 2px",
                }}
              >
                Previous Allocation:
              </div>
              {si.previous_allocation.map((block: any, idx: number) =>
                renderSubFundTable(block, `prev-${idx}`),
              )}
            </>
          )}

        {Array.isArray(si.vps_errors) && si.vps_errors.length > 0 && (
          <div style={{ fontSize: "9px", color: "#c0392b", marginTop: "2px" }}>
            {si.vps_errors.length} allocation error(s) flagged
          </div>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Totals footer (sums the flat numeric fields, same pattern as the
  // generic report's tfoot logic).
  // -------------------------------------------------------------------------
  const computeTotals = (data: any[]) => {
    const totalsObj: Record<string, number> = {};
    TOTAL_ROW_FIELDS.forEach((col) => (totalsObj[col] = 0));
    data.forEach((item: any) => {
      TOTAL_ROW_FIELDS.forEach((col) => {
        totalsObj[col] += parseFloat(item[col] || "0") || 0;
      });
    });
    setTotals(totalsObj);
  };

  // -------------------------------------------------------------------------
  // Search — identical fetch pattern to the generic report
  // (getTransactionReportSearchData(email, fields)) but txn_type is always
  // one of the 3 restricted VPS types.
  // -------------------------------------------------------------------------
  const searchData = async () => {
    if (!fields.txn_type) {
      toast.error("Please select a Transaction Type");
      return;
    }
    setLoading(true);
    try {
      const searchResponse = await getTransactionReportSearchData(
        email,
        fields,
      );

      setResAmc(fields.amc_code);
      setResFund(fields.fund_code);
      setFromDateRes(fields.from_date);
      setToDateRes(fields.to_date);
      setTxnTypeRes(fields.txn_type);
      setStatusRes(fields.status);

      const data = searchResponse.data.data || [];
      setReportTxn(data);

      const cols = VPS_COLUMNS[fields.txn_type] || [];
      setDropdownList(cols);
      setAllDropdownColumns(cols);

      computeTotals(data);
      setLoading(false);
    } catch (err: any) {
      if (err.response !== undefined) {
        toast.error(err.response.data.message);
      } else {
        toast.error(err.message);
      }
      setReportTxn([]);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Container fluid>
              <ToastContainer limit={1} />
              <div className="">
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">VPS Transaction Report</h4>
                  </div>
                  <div className="card-body">
                    <Row>
                      <Col md="4">
                        <div className="form-group">
                          <label>
                            AMC <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={allAmc}
                            onChange={(selected: any) => {
                              setAmcLabel(selected || "");
                              setFields({
                                ...fields,
                                amc_code: selected?.value || "",
                              });
                              getFunds(selected?.value);
                            }}
                            isClearable={true}
                            placeholder="Select AMC"
                            value={amcLabel}
                            styles={customStyles}
                          />
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="form-group">
                          <label>Select Fund</label>
                          <select
                            className="form-control"
                            onChange={(e) =>
                              setFields({
                                ...fields,
                                fund_code: e.target.value,
                              })
                            }
                          >
                            <option value="" defaultChecked hidden>
                              Select Fund
                            </option>
                            {renderFundsDropDown()}
                          </select>
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="form-group">
                          <label>
                            Transaction Type{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-control"
                            value={fields.txn_type}
                            onChange={(e) =>
                              setFields({ ...fields, txn_type: e.target.value })
                            }
                          >
                            <option value="" defaultChecked hidden>
                              Select Type
                            </option>
                            {VPS_TXN_TYPES.map((t) => (
                              <option key={t.api_name} value={t.api_name}>
                                {t.dis_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="form-group">
                          <label>Transaction Status</label>
                          <select
                            className="form-control"
                            value={fields.status}
                            onChange={(e) =>
                              setFields({ ...fields, status: e.target.value })
                            }
                          >
                            <option value="" defaultChecked hidden>
                              Select type
                            </option>
                            <option value="CREATED">Created</option>
                            <option value="COMPLIANCE">Compliance</option>
                            <option value="INREVIEW">In Review</option>
                            <option value="ACCEPTED-AMC">Accepted AMC</option>
                            <option value="ACCEPTED-TRUSTEE">
                              Accepted Trustee
                            </option>
                            <option value="COMPLETED">Completed</option>
                            <option value="REJECTED-TRUSTEE">
                              Rejected Trustee
                            </option>
                          </select>
                        </div>
                      </Col>

                      <Col md="4">
                        <div className="form-group">
                          <label>Date From</label>
                          <input
                            type="date"
                            value={fields.from_date}
                            className="form-control"
                            onChange={(e) =>
                              setFields({
                                ...fields,
                                from_date: e.target.value,
                              })
                            }
                          />
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="form-group">
                          <label>Date To</label>
                          <input
                            type="date"
                            value={fields.to_date}
                            className="form-control"
                            onChange={(e) =>
                              setFields({ ...fields, to_date: e.target.value })
                            }
                          />
                        </div>
                      </Col>
                    </Row>

                    <div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => searchData()}
                        disabled={Boolean(loading)}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border login-txt spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span className="login-txt"> Loading...</span>
                          </>
                        ) : (
                          <span>Search</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {reportTxn.length > 0 && (
                  <Row>
                    <Col md="8"></Col>
                    <Col md="4">
                      <div className="mt-3 mr-3">
                        <MultiSelect
                          options={allDropdownColumns}
                          value={dropdownList}
                          onChange={setDropdownList}
                          labelledBy={"Select Columns"}
                          className="multi-select"
                          overrideStrings={{
                            selectAll: "Select all Columns",
                            selectSomeItems: "Select Columns",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                )}

                <div className="card mt-3">
                  <div className="card-body">
                    <div
                      id="vpsPrintDiv"
                      className="p-2"
                      style={{ background: "white", color: "black" }}
                    >
                      <PDFExport
                        paperSize="A4"
                        margin="1cm"
                        scale={0.5}
                        repeatHeaders={true}
                        landscape={true}
                        fileName={"VPS-Transaction-Report-" + date + ".pdf"}
                        ref={pdfExportComponent}
                      >
                        {reportTxn.length > 0 ? (
                          <>
                            <ReportHeader title="VPS Transaction Report" />
                            <hr />
                            <Row>
                              <Col md="4" style={{ marginLeft: "70px" }}>
                                <Row>
                                  <Col md="5">
                                    <span style={{ fontWeight: "bold" }}>
                                      AMC:
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <span>{resAmc}</span>
                                  </Col>
                                </Row>
                              </Col>

                              <Col md="4" style={{ marginLeft: "70px" }}>
                                <Row>
                                  <Col md="5">
                                    <span style={{ fontWeight: "bold" }}>
                                      Fund:
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <span>{resFund}</span>
                                  </Col>
                                </Row>
                              </Col>

                              {txnTypeRes !== "" && (
                                <Col md="4" style={{ marginLeft: "70px" }}>
                                  <Row>
                                    <Col md="5">
                                      <span
                                        className="text-nowrap"
                                        style={{ fontWeight: "bold" }}
                                      >
                                        Transaction Type:
                                      </span>
                                    </Col>
                                    <Col md="6">
                                      <span>{CapTxnType(txnTypeRes)}</span>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              {fromDateRes !== "" && (
                                <Col md="4" style={{ marginLeft: "70px" }}>
                                  <Row>
                                    <Col md="5">
                                      <span style={{ fontWeight: "bold" }}>
                                        From Date:
                                      </span>
                                    </Col>
                                    <Col md="6">
                                      <span>
                                        {moment(fromDateRes).format(
                                          "YYYY-MM-DD",
                                        )}
                                      </span>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              {toDateRes !== "" && (
                                <Col md="4" style={{ marginLeft: "70px" }}>
                                  <Row>
                                    <Col md="5">
                                      <span style={{ fontWeight: "bold" }}>
                                        To Date:
                                      </span>
                                    </Col>
                                    <Col md="6">
                                      <span>
                                        {moment(toDateRes).format(
                                          "YYYY-MM-DD",
                                        ) === "Invalid date"
                                          ? toDateRes
                                          : moment(toDateRes).format(
                                              "YYYY-MM-DD",
                                            )}
                                      </span>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              {statusRes !== "" && (
                                <Col md="4" style={{ marginLeft: "70px" }}>
                                  <Row>
                                    <Col md="5">
                                      <span style={{ fontWeight: "bold" }}>
                                        Status:
                                      </span>
                                    </Col>
                                    <Col md="6">
                                      <span>{statusRes}</span>
                                    </Col>
                                  </Row>
                                </Col>
                              )}

                              <Col md="4" style={{ marginLeft: "70px" }}>
                                <Row>
                                  <Col md="5">
                                    <span style={{ fontWeight: "bold" }}>
                                      Report Date:
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <span>
                                      {moment(date).format("YYYY-MM-DD")}
                                    </span>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>

                            <div
                              style={{ overflowX: "auto", overflowY: "hidden" }}
                            >
                              <table
                                className="report"
                                style={{ color: "black" }}
                              >
                                <thead style={{ alignSelf: "center" }}>
                                  <tr>
                                    {dropdownList.map(
                                      (item: any, index: number) => (
                                        <th key={index}>
                                          <span
                                            style={{
                                              textTransform: "capitalize",
                                            }}
                                          >
                                            {item.label.toString()}
                                          </span>
                                        </th>
                                      ),
                                    )}
                                    <th>VPS Fund Allocation</th>
                                  </tr>
                                </thead>

                                <tbody className="report-table-body">
                                  {reportTxn.map(
                                    (transaction: any, index: number) => (
                                      <tr key={index}>
                                        {dropdownList.map(
                                          (item: any, i: number) => (
                                            <td key={i}>
                                              {formatCellValue(
                                                item.value,
                                                transaction,
                                              )}
                                            </td>
                                          ),
                                        )}
                                        <td>
                                          {renderVpsAllocation(transaction)}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>

                                <tfoot>
                                  <tr>
                                    {dropdownList.map(
                                      (item: any, i: number) => {
                                        if (
                                          TOTAL_ROW_FIELDS.includes(item.value)
                                        ) {
                                          const raw = totals[item.value] ?? 0;
                                          const formatted = raw.toFixed(
                                            item.value === "units" ? 4 : 2,
                                          );
                                          return (
                                            <td key={i}>
                                              <strong>
                                                {numberWithCommas(formatted)}
                                              </strong>
                                            </td>
                                          );
                                        }
                                        return <td key={i}> </td>;
                                      },
                                    )}
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </>
                        ) : (
                          <div className="table-empty">
                            <BsTable className="icon" />
                            <p className="text-center empty-text">
                              No record found
                            </p>
                          </div>
                        )}
                      </PDFExport>
                    </div>
                    <button
                      className="btn btn-primary mt-3"
                      onClick={() => {
                        if (pdfExportComponent.current) {
                          pdfExportComponent.current.save();
                        }
                      }}
                    >
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </Container>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default VpsTransactionReportScreen;
